"use client";

import { Button } from "@klarify/ui/components/ui/button";
import { cn } from "@klarify/ui/lib/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import Image from "next/image";
import GoogleGIcon from "../svg/google-g-icon";

const AUTHOR_PHOTO = "/avif/testimonial.avif";

const COPYRIGHT_YEAR = new Date().getFullYear();

const EASE_OUT = [0.22, 1, 0.36, 1] as const;
const EASE_OVERSHOOT = [0.34, 1.56, 0.64, 1] as const;

const CREAM_GRADIENT =
	"linear-gradient(165deg, var(--color-klarify-peach-50) 0%, var(--color-klarify-peach-100) 55%, var(--color-klarify-peach-200) 100%)";

const AuthorAvatar = ({
	size,
	alt,
	className,
}: {
	size: number;
	alt: string;
	className?: string;
}) => (
	<Image
		src={AUTHOR_PHOTO}
		alt={alt}
		width={size}
		height={size}
		className={cn(
			"shrink-0 rounded-full object-cover shadow-[inset_0_0_0_2px_rgba(255,255,255,0.15)]",
			className,
		)}
		style={{ width: size, height: size }}
	/>
);

const fade = (delay: number) => ({
	initial: { opacity: 0, y: 8 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.55, delay, ease: EASE_OUT },
});

const FilledStars = ({
	count = 5,
	size,
	className,
	stagger = false,
}: {
	count?: number;
	size: number;
	className?: string;
	stagger?: boolean;
}) => {
	const { t } = useLingui();
	return (
		<span
			role="img"
			className={cn("inline-flex gap-[2px] text-klarify-peach-500", className)}
			aria-label={t`${{ count: count }} out of ${{ count: count }} stars`}
		>
			{Array.from({ length: count }, (_, i) => {
				const star = (
					<Star
						fill="currentColor"
						strokeWidth={0}
						style={{ width: size, height: size }}
					/>
				);
				// Plain spans when static: the desktop LCP panel must not hydrate
				// framer-motion or SSR at opacity 0.
				return stagger ? (
					<motion.span
						key={i}
						initial={{ opacity: 0, scale: 0.6 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{
							duration: 0.35,
							delay: 0.55 + i * 0.06,
							ease: EASE_OUT,
						}}
						className="inline-flex"
					>
						{star}
					</motion.span>
				) : (
					<span
						key={i}
						className="inline-flex"
					>
						{star}
					</span>
				);
			})}
		</span>
	);
};

export function SignupTestimonialDesktop({
	className,
}: {
	className?: string;
}) {
	const { t } = useLingui();
	// Plain elements on purpose: this panel is the desktop LCP element, so it
	// carries no framer-motion hydration and no entrance animation.
	return (
		<aside
			className={cn(
				"relative flex flex-col overflow-hidden px-12 pt-12 pb-11 xl:px-16 2xl:px-22 2xl:pt-14 2xl:pb-13",
				className,
			)}
			style={{ background: CREAM_GRADIENT }}
		>
			<div className="flex items-center gap-2.5 text-klarify-ocean-700">
				<div className="grid h-8 w-8 place-items-center rounded-lg border border-klarify-ocean-500/10 bg-white">
					<Image
						src="/svg/happy-klarify-plant.svg"
						alt=""
						width={22}
						height={22}
						className="h-[22px] w-[22px]"
					/>
				</div>
				<span className="font-semibold text-lg tracking-tight">
					<Trans>klarify</Trans>
				</span>
			</div>

			<div className="flex max-w-[560px] flex-1 flex-col justify-center pt-7">
				<span className="mb-[18px] inline-flex items-center gap-2 font-semibold text-[11px] text-klarify-ocean-600 uppercase tracking-[0.14em]">
					<span className="relative inline-block h-1.5 w-1.5 rounded-full bg-klarify-ocean-500 shadow-[0_0_0_4px_rgba(0,70,74,0.15)]" />
					<Trans>Why therapists choose Klarify</Trans>
				</span>

				<div className="relative max-w-[520px] rounded-3xl bg-white p-9 pb-8 shadow-[0_30px_60px_-20px_rgba(40,20,10,0.18),0_8px_18px_-8px_rgba(40,20,10,0.12)]">
					<span className="absolute -top-[18px] -left-[18px] origin-bottom-left rounded-full bg-klarify-ocean-500 px-3.5 py-2 font-semibold text-[11px] text-white uppercase tracking-[0.08em] shadow-[0_8px_16px_-6px_rgba(0,70,74,0.5)]">
						<Trans>Therapist story</Trans>
					</span>

					<div className="mb-4">
						{/* No stagger: this panel is the desktop LCP element and must not SSR at opacity 0. */}
						<FilledStars size={16} />
					</div>

					<p className="mb-7 text-balance font-serif text-[26px] text-klarify-ocean-900 leading-[1.32] tracking-tight">
						<Trans>
							Klarify has changed my life as a counsellor. It allows me to be{" "}
						</Trans>
						<em className="text-klarify-peach-700 italic">
							<Trans>fully present and more relaxed</Trans>
						</em>
						<Trans>
							{" "}
							when I am in session. It has changed not only how I work, but how
							I live.
						</Trans>
					</p>

					<div className="flex items-center gap-3.5">
						<AuthorAvatar
							size={56}
							alt={t`Kelly Copeland, M.Ed Counselling, RCT, CCC, CTRS`}
						/>
						<div className="leading-tight">
							<div className="font-semibold text-[15px] text-klarify-ocean-900">
								<Trans>Kelly Copeland</Trans>
							</div>
							<div className="text-[13px] text-klarify-ocean-600">
								<Trans>M.Ed Counselling, RCT, CCC, CTRS</Trans>
							</div>
							<div className="text-[13px] text-klarify-ocean-600">
								<Trans>Lifemark</Trans>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="mt-9 flex items-center justify-between gap-3 text-[12px] text-klarify-ocean-700/85">
				<span>
					<Trans>
						© Klarify {{ year: COPYRIGHT_YEAR }} · HIPAA, PHIPA and PIPEDA
						compliant
					</Trans>
				</span>
				<Image
					src="/svg/content-klarify-plant.svg"
					alt=""
					width={56}
					height={56}
					className="opacity-90"
				/>
			</div>
		</aside>
	);
}

export function SignupTestimonialMobileMini({
	className,
}: {
	className?: string;
}) {
	const { t } = useLingui();
	return (
		<motion.div
			initial={{ opacity: 0, y: -8 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.45, ease: EASE_OUT }}
			className={cn(
				"relative w-full overflow-hidden rounded-2xl px-4 pt-4 pb-4",
				className,
			)}
			style={{ background: CREAM_GRADIENT }}
		>
			<div className="mb-3 flex items-center justify-between text-klarify-ocean-700">
				<div className="flex items-center gap-2">
					<div className="grid h-6 w-6 place-items-center rounded-md border border-klarify-ocean-500/10 bg-white">
						<Image
							src="/svg/happy-klarify-plant.svg"
							alt=""
							width={16}
							height={16}
							className="h-4 w-4"
						/>
					</div>
					<span className="font-semibold text-[14px]">
						<Trans>klarify</Trans>
					</span>
				</div>
				<span className="inline-flex items-center gap-1.5 font-semibold text-[10px] text-klarify-ocean-600 uppercase tracking-[0.14em]">
					<span className="relative inline-block h-[5px] w-[5px] rounded-full bg-klarify-ocean-500 shadow-[0_0_0_3px_rgba(0,70,74,0.15)]" />
					<Trans>Trusted by therapists</Trans>
				</span>
			</div>

			<motion.div
				initial={{ opacity: 0, y: 8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.12,
					ease: EASE_OUT,
				}}
				className="flex gap-3 rounded-2xl border border-klarify-ocean-500/10 bg-white/70 p-3.5 shadow-[0_8px_20px_-10px_rgba(40,20,10,0.12)] backdrop-blur"
			>
				<AuthorAvatar
					size={36}
					alt={t`Kelly Copeland, M.Ed Counselling, RCT, CCC, CTRS`}
				/>
				<div className="min-w-0 flex-1">
					<p className="mb-1.5 text-balance font-serif text-[14px] text-klarify-ocean-900 leading-[1.35]">
						<Trans>“It allows me to be </Trans>
						<em className="text-klarify-peach-700 italic">
							<Trans>fully present and more relaxed</Trans>
						</em>
						<Trans> when I am in session.”</Trans>
					</p>
					<div className="flex items-center gap-1.5 text-[11px] text-klarify-ocean-600">
						<b className="font-semibold text-klarify-ocean-900">
							<Trans>Kelly Copeland</Trans>
						</b>
						<FilledStars
							size={10}
							className="ml-auto"
						/>
					</div>
				</div>
			</motion.div>
		</motion.div>
	);
}

export function SignupWelcomeMobile({
	onCreateAccount,
	onContinueWithGoogle,
	signInUrl,
	busy,
	googleDisabled,
	className,
}: {
	onCreateAccount: () => void;
	onContinueWithGoogle: () => void;
	signInUrl?: string;
	busy?: boolean;
	/** Google needs Clerk; the create-account CTA does not, so `busy` alone cannot gate it. */
	googleDisabled?: boolean;
	className?: string;
}) {
	const { t } = useLingui();
	return (
		<motion.div
			// The overlay paints from SSR above the register card, so its own frame
			// must not start at opacity 0; the children keep their entrance staggers.
			initial={false}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.3 }}
			className={cn("fixed inset-0 z-30 overflow-y-auto", className)}
			style={{ background: CREAM_GRADIENT }}
		>
			<div className="flex min-h-full flex-col px-6 pt-7 pb-10">
				<motion.div
					{...fade(0)}
					className="flex items-center gap-2 text-klarify-ocean-700"
				>
					<div className="grid h-8 w-8 place-items-center rounded-lg border border-klarify-ocean-500/10 bg-white">
						<Image
							src="/svg/happy-klarify-plant.svg"
							alt=""
							width={20}
							height={20}
							className="h-5 w-5"
						/>
					</div>
					<span className="font-semibold text-base tracking-tight">
						<Trans>klarify</Trans>
					</span>
				</motion.div>

				<div className="flex flex-1 flex-col justify-center gap-5 py-6">
					<motion.span
						{...fade(0.1)}
						className="inline-flex items-center gap-2 font-semibold text-[11px] text-klarify-ocean-600 uppercase tracking-[0.14em]"
					>
						<span className="relative inline-block h-1.5 w-1.5 rounded-full bg-klarify-ocean-500 shadow-[0_0_0_4px_rgba(0,70,74,0.15)]" />
						<Trans>Why therapists choose Klarify</Trans>
					</motion.span>

					<motion.div
						initial={{ opacity: 0, y: 14 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 0.6,
							delay: 0.15,
							ease: EASE_OUT,
						}}
						className="relative rounded-[22px] bg-white px-[22px] pt-[26px] pb-[22px] shadow-[0_22px_44px_-16px_rgba(40,20,10,0.18),0_6px_14px_-6px_rgba(40,20,10,0.12)]"
					>
						<motion.span
							initial={{ opacity: 0, scale: 0.85, y: -4 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							transition={{
								duration: 0.4,
								delay: 0.3,
								ease: EASE_OVERSHOOT,
							}}
							className="absolute -top-3 left-[18px] origin-bottom-left rounded-full bg-klarify-ocean-500 px-2.5 py-1.5 font-semibold text-[10px] text-white uppercase tracking-[0.08em] shadow-[0_6px_12px_-4px_rgba(0,70,74,0.5)]"
						>
							<Trans>Therapist story</Trans>
						</motion.span>

						<div className="mb-3">
							<FilledStars
								size={14}
								stagger
							/>
						</div>

						<p className="mb-5 text-balance font-serif text-[20px] text-klarify-ocean-900 leading-[1.32] tracking-tight">
							<Trans>
								Klarify has changed my life as a counsellor. It allows me to be{" "}
							</Trans>
							<em className="text-klarify-peach-700 italic">
								<Trans>fully present and more relaxed</Trans>
							</em>
							<Trans>
								{" "}
								when I am in session. It has changed not only how I work, but
								how I live.
							</Trans>
						</p>

						<div className="flex items-center gap-3">
							<AuthorAvatar
								size={44}
								alt={t`Kelly Copeland, M.Ed Counselling, RCT, CCC, CTRS`}
							/>
							<div className="leading-tight">
								<div className="font-semibold text-[13px] text-klarify-ocean-900">
									<Trans>Kelly Copeland</Trans>
								</div>
								<div className="text-[12px] text-klarify-ocean-600">
									<Trans>M.Ed Counselling, RCT, CCC, CTRS</Trans>
								</div>
								<div className="text-[12px] text-klarify-ocean-600">
									<Trans>Lifemark</Trans>
								</div>
							</div>
						</div>
					</motion.div>

					<motion.div
						{...fade(0.4)}
						className="flex flex-col gap-2.5 pt-2"
					>
						<Button
							type="button"
							onClick={onCreateAccount}
							disabled={busy}
							className="h-12 w-full font-semibold text-[15px] shadow-[0_8px_24px_-8px_rgba(0,70,74,0.5)] active:scale-[0.99]"
						>
							<Trans>Create your account</Trans>
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={onContinueWithGoogle}
							disabled={busy || googleDisabled}
							aria-busy={googleDisabled}
							className="h-12 w-full gap-2.5 border-klarify-ocean-500/10 font-medium text-[15px] text-klarify-ocean-900 active:scale-[0.99]"
						>
							<GoogleGIcon
								width={18}
								height={18}
							/>
							<Trans>Continue with Google</Trans>
						</Button>
					</motion.div>
				</div>

				<motion.div
					{...fade(0.55)}
					className="text-center text-[12px] text-klarify-ocean-700"
				>
					<Trans>Already have an account?</Trans>{" "}
					<a
						href={signInUrl}
						className="font-semibold text-klarify-ocean-700 underline underline-offset-2"
					>
						<Trans>Sign in</Trans>
					</a>
				</motion.div>
			</div>
		</motion.div>
	);
}
